import { LeadActivity, LeadActivityType } from "views/leads-id/types";
import {
  formatShowingDateTime,
  timezoneOptions
} from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/showing-time-format";

export const SHOWING_RESCHEDULE_REQUEST_ACTIVITY_TYPES: string[] = [
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_SENT,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_RESENT,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_REPLACED,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_WITHDRAWN,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_CLOSED,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_OPENED,
  LeadActivityType.SHOWING_RESCHEDULED_BY_LEAD,
  LeadActivityType.SHOWING_RESCHEDULE_OTHER_TIMES_REQUESTED,
  LeadActivityType.SHOWING_RESCHEDULE_REQUESTED_BY_LEAD,
  LeadActivityType.SHOWING_CANCELLED_FROM_RESCHEDULE_PAGE,
  LeadActivityType.SHOWING_RESCHEDULE_REMINDER_SENT,
  LeadActivityType.SHOWING_RESCHEDULE_REQUEST_EXPIRED
];

export const isShowingRescheduleRequestActivity = (activityType: string) =>
  SHOWING_RESCHEDULE_REQUEST_ACTIVITY_TYPES.includes(activityType);

export type RescheduleLineIcon =
  | "calendar"
  | "clock"
  | "note"
  | "sent"
  | "message"
  | "check"
  | "assignee"
  | "info"
  | "bell";

type RescheduleDescriptionLine = {
  key: string;
  icon: RescheduleLineIcon;
  text: string;
  /** A previous value shown struck through after the text (e.g. the old time). */
  struck?: string;
  tone: "muted" | "danger";
  linkify?: boolean;
};

const SENT_VIA_LABELS: Record<string, string> = {
  sms: "text",
  email: "email"
};

const CLOSE_REASONS: Record<string, string> = {
  team_rescheduled: "The team picked a new time",
  showing_cancelled: "The showing was canceled"
};

const EXPIRY_REASONS: Record<string, string> = {
  time_started: "The showing time started",
  showing_expired: "The showing expired"
};

type Metadata = LeadActivity["metadata"];

const line = (
  key: string,
  icon: RescheduleLineIcon,
  text: string,
  tone: RescheduleDescriptionLine["tone"] = "muted"
): RescheduleDescriptionLine => ({ key, icon, text, tone });

const nonBlank = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

const firstName = (name: string | null): string | null =>
  name ? (name.split(/\s+/)[0] ?? null) : null;

// "Tue 10/13 4:30 PM"
const formatCompact = (
  iso: string | null | undefined,
  timezone: string | null | undefined
): string | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms)
    .toLocaleString("en-US", {
      weekday: "short",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      // The list stays compact: the zone name is on the current-time line.
      timeZone: timezoneOptions(timezone).timeZone
    })
    .replace(/,/g, "");
};

const offerLines = (metadata: Metadata): RescheduleDescriptionLine[] => {
  const { offers, timezone } = metadata;
  if (typeof offers === "string")
    return nonBlank(offers) ? [line("offers", "calendar", offers)] : [];
  if (!Array.isArray(offers)) return [];

  const labels = offers.flatMap((offer) => {
    if (!offer || typeof offer !== "object") return [];
    const time = formatCompact(offer.start_time, timezone);
    if (!time) return [];
    const holder = firstName(nonBlank(offer.holder_name));
    return [holder ? `${time} (${holder})` : time];
  });
  return labels.length > 0
    ? [line("offers", "calendar", `Offered ${labels.join(" • ")}`)]
    : [];
};

const sentViaLine = (metadata: Metadata): RescheduleDescriptionLine[] => {
  const channels = Array.isArray(metadata.sent_via) ? metadata.sent_via : [];
  const labels = channels
    .map((channel) => SENT_VIA_LABELS[channel])
    .filter((label): label is string => Boolean(label));
  const unique = Array.from(new Set(labels));
  return unique.length > 0
    ? [line("sent-via", "sent", `Sent by ${unique.join(" and ")}`)]
    : [];
};

const sentLines = (metadata: Metadata): RescheduleDescriptionLine[] => {
  const current = formatShowingDateTime(
    metadata.current_start_time,
    metadata.timezone
  );
  const note = nonBlank(metadata.note);
  const message = nonBlank(metadata.message);
  return [
    ...(current ? [line("current", "clock", `Current time ${current}`)] : []),
    ...offerLines(metadata),
    ...(note ? [line("note", "note", `“${note}”`)] : []),
    ...sentViaLine(metadata),
    ...(message
      ? [{ ...line("message", "message", message), linkify: true }]
      : []),
    ...(metadata.sms_delivered === false
      ? [line("sms-not-delivered", "info", "Text not delivered", "danger")]
      : [])
  ];
};

// "Wed, Oct 14 at 10:00 AM ~~Mon, Oct 12 at 1:00 PM~~"
const movedLine = (
  key: string,
  newIso: string | null | undefined,
  oldIso: string | null | undefined,
  timezone: string | null | undefined
): RescheduleDescriptionLine[] => {
  const next = formatShowingDateTime(newIso, timezone);
  if (!next) return [];
  const previous = formatShowingDateTime(oldIso, timezone);
  return [{ ...line(key, "calendar", next), struck: previous ?? undefined }];
};

const rescheduledByLeadLines = (
  metadata: Metadata
): RescheduleDescriptionLine[] => {
  const previous = nonBlank(metadata.previous_partner_user_name);
  const next = nonBlank(metadata.new_partner_user_name);
  return [
    ...movedLine(
      "moved",
      metadata.new_start_time,
      metadata.old_start_time,
      metadata.timezone
    ),
    ...(metadata.confirmed_instantly === true
      ? [line("confirmed", "check", "Confirmed instantly")]
      : []),
    ...(previous && next
      ? [line("assignee", "assignee", `Assigned to ${next}, was ${previous}`)]
      : [])
  ];
};

const requestedByLeadLines = (
  metadata: Metadata
): RescheduleDescriptionLine[] => [
  ...movedLine(
    "requested",
    metadata.new_start_time,
    metadata.old_start_time,
    metadata.timezone
  ),
  line("waiting", "clock", "Waiting for approval")
];

const reasonLine = (
  reasons: Record<string, string>,
  reason: string | undefined
): RescheduleDescriptionLine[] => {
  const text = reason ? reasons[reason] : undefined;
  return text ? [line("reason", "info", text)] : [];
};

const reminderLines = (metadata: Metadata): RescheduleDescriptionLine[] => {
  const name = nonBlank(metadata.partner_user_name);
  return name ? [line("reminder", "bell", `Sent to ${name}`)] : [];
};

const cancelledLines = (metadata: Metadata): RescheduleDescriptionLine[] => {
  const time = formatShowingDateTime(metadata.start_time, metadata.timezone);
  return time
    ? [{ ...line("showing-time", "calendar", ""), struck: time }]
    : [];
};

export const describeShowingRescheduleRequest = (
  activity: Pick<LeadActivity, "activity_type" | "metadata">
): RescheduleDescriptionLine[] => {
  const metadata = activity.metadata ?? {};
  switch (activity.activity_type) {
    case LeadActivityType.SHOWING_RESCHEDULE_REQUEST_SENT:
    case LeadActivityType.SHOWING_RESCHEDULE_REQUEST_RESENT:
      return sentLines(metadata);
    case LeadActivityType.SHOWING_RESCHEDULED_BY_LEAD:
      return rescheduledByLeadLines(metadata);
    case LeadActivityType.SHOWING_RESCHEDULE_REQUESTED_BY_LEAD:
      return requestedByLeadLines(metadata);
    case LeadActivityType.SHOWING_RESCHEDULE_REQUEST_CLOSED:
      return reasonLine(CLOSE_REASONS, metadata.reason);
    case LeadActivityType.SHOWING_RESCHEDULE_REQUEST_EXPIRED:
      return reasonLine(EXPIRY_REASONS, metadata.reason);
    case LeadActivityType.SHOWING_RESCHEDULE_REMINDER_SENT:
      return reminderLines(metadata);
    case LeadActivityType.SHOWING_CANCELLED_FROM_RESCHEDULE_PAGE:
      return cancelledLines(metadata);
    default:
      return [];
  }
};
